const MULTI_SELECT_FIELD_TYPES = new Set([
    'multipleValueList',
    'fuzzySearchMultiple',
    'multiSelect',
]);

export const isMultiSelectFieldType = (type) => MULTI_SELECT_FIELD_TYPES.has(type);

const hasSelectObjectWithId = (raw) =>
    raw != null &&
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    Object.prototype.hasOwnProperty.call(raw, 'value') &&
    raw.value !== undefined &&
    raw.value !== null;

const toSelectOption = (raw) => {
    if (hasSelectObjectWithId(raw)) {
        const label = raw.description ?? raw.label ?? String(raw.value);
        return { label, value: String(raw.value) };
    }
    if (raw === null || raw === undefined || raw === '') return undefined;
    return { label: String(raw), value: String(raw) };
};

export const parseMultiSelectIds = (raw) => {
    if (raw === null || raw === undefined || raw === '') return [];

    if (Array.isArray(raw)) {
        return raw
            .map((item) => {
                if (hasSelectObjectWithId(item)) return String(item.value);
                if (item === null || item === undefined || item === '') return null;
                return String(item);
            })
            .filter(Boolean);
    }

    if (typeof raw === 'string') {
        return raw
            .split(';')
            .map((part) => part.trim())
            .filter(Boolean);
    }

    if (hasSelectObjectWithId(raw)) {
        return String(raw.value)
            .split(';')
            .map((part) => part.trim())
            .filter(Boolean);
    }

    return [String(raw)];
};

/** Value shape expected by HoiPoi Select when `isMulti` is true (always an array). */
export const normalizeMultiSelectValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }
    if (Array.isArray(value)) {
        return value.length ? value : undefined;
    }
    const parsed = parseMultiSelectOptions(value);
    return parsed.length ? parsed : undefined;
};

export const parseMultiSelectOptions = (raw) => {
    if (raw === null || raw === undefined || raw === '') return [];

    if (Array.isArray(raw)) {
        return raw.map((item) => toSelectOption(item)).filter(Boolean);
    }

    if (hasSelectObjectWithId(raw)) {
        const option = toSelectOption(raw);
        return option ? [option] : [];
    }

    if (typeof raw === 'object' && raw.description && raw.value !== undefined) {
        const labels = String(raw.description).split(';');
        const ids = parseMultiSelectIds(raw.value);
        if (ids.length) {
            return ids.map((id, index) => ({
                value: id,
                label: (labels[index] || labels[0] || id).trim(),
            }));
        }
    }

    const ids = parseMultiSelectIds(raw);
    return ids.map((id) => ({ value: id, label: id }));
};

export const serializeMultiSelectValue = (value) => {
    if (!value) return null;
    if (!Array.isArray(value) || value.length === 0) return null;

    const ids = value
        .map((item) => {
            if (item === null || item === undefined || item === '') return null;
            if (typeof item === 'object') return item.value ?? item.Id ?? item.id;
            return item;
        })
        .filter((id) => id !== null && id !== undefined && id !== '');

    if (!ids.length) return null;
    return `;${ids.map(String).join(';')};`;
};

export const resolveRowFieldValue = (rowData, fieldName) => {
    if (!rowData || !fieldName) return undefined;
    return (
        rowData[fieldName] ?? rowData[fieldName.toLowerCase()] ?? rowData[fieldName.toUpperCase()]
    );
};

export const resolveParentValueId = (parentValue) => {
    if (parentValue === null || parentValue === undefined || parentValue === '') return null;
    if (typeof parentValue === 'object') {
        return parentValue.value ?? parentValue.Id ?? parentValue.id ?? null;
    }
    return parentValue;
};

export const defaultDependenceFilter = (option, parentValueId) => {
    const parentId = resolveParentValueId(parentValueId);
    if (parentId === null || parentId === undefined || parentId === '') return true;
    return (
        String(option.idparent) === String(parentId) ||
        option.idparent === '-1' ||
        option.idparent === '0' ||
        option.idparent === '' ||
        option.idparent === undefined ||
        option.idparent === null
    );
};

const buildNumSelectedLiteral = (countLabel) => `%@ ${countLabel}`;

/** Resolves the column label shown in multiselect chip/link popovers. */
export const resolveMultiSelectColumnLabel = (params, fieldLabelFromInput) =>
    fieldLabelFromInput ||
    params?.inputParams?.fieldLabel ||
    params?.cellRendererParams?.fieldLabel ||
    params?.colDef?.headerName ||
    '';

export const buildMultiSelectSummaryText = (count, fieldLabel) => {
    const safeCount = Number(count) || 0;
    if (!safeCount) return '';
    if (fieldLabel) return `${safeCount} ${fieldLabel}`;
    return String(safeCount);
};

/** Edit-mode chip label: one value → label; multiple → "First, +N". */
export const buildMultiSelectChipText = (labels) => {
    const safeLabels = (labels || []).filter(Boolean);
    if (!safeLabels.length) return '';
    if (safeLabels.length === 1) return safeLabels[0];
    return `${safeLabels[0]}, +${safeLabels.length - 1}`;
};

/** True when the row has no multiselect value (defaults may apply). */
export const isEmptyMultiSelectRaw = (raw) => {
    if (raw === null || raw === undefined || raw === '') return true;
    if (Array.isArray(raw) && raw.length === 0) return true;
    return false;
};

/** Stable id key for comparing multiselect values (order preserved). */
export const multiSelectIdsKey = (raw) => parseMultiSelectIds(raw).join('|');

/**
 * When react-select runs without `isMulti`, onChange receives a single option object.
 * Merge toggles into an array so the grid still accumulates multiple values.
 */
export const resolveMultiSelectOnChange = (currentValue, newValue, actionMeta) => {
    if (actionMeta?.action === 'clear') {
        return undefined;
    }
    if (Array.isArray(newValue)) {
        return newValue.length ? newValue : undefined;
    }
    if (
        newValue &&
        typeof newValue === 'object' &&
        newValue.value !== undefined &&
        actionMeta?.action === 'select-option'
    ) {
        const currentArr = normalizeMultiSelectValue(currentValue) || [];
        const nextId = String(newValue.value);
        const exists = currentArr.some((option) => String(option.value) === nextId);
        if (exists) {
            const filtered = currentArr.filter((option) => String(option.value) !== nextId);
            return filtered.length ? filtered : undefined;
        }
        return [...currentArr, newValue];
    }
    return newValue;
};

/** Stable key suffix so Select remounts when per-row smart field props change (ag-grid may skip cell refresh). */
export const buildMultiSelectSmartFieldStateKey = (lineOverrides) => {
    if (!lineOverrides || typeof lineOverrides !== 'object') return '';
    const parts = [];
    if (lineOverrides.readOnly) parts.push('ro');
    if (lineOverrides.hidden) parts.push('hd');
    if (lineOverrides.mandatory !== undefined) parts.push(`m${lineOverrides.mandatory ? 1 : 0}`);
    return parts.join('-');
};

/** Ensures current selections stay visible in the menu after async list/fuzzy loads. */
export const mergeMultiSelectOptionsWithSelection = (options, selected) => {
    const list = Array.isArray(options) ? options : [];
    const selectedOptions = normalizeMultiSelectValue(selected) || [];
    if (!selectedOptions.length) return list;

    const seen = new Set(list.map((option) => String(option?.value)));
    const missing = selectedOptions.filter(
        (option) => option?.value != null && !seen.has(String(option.value)),
    );
    return missing.length ? [...missing, ...list] : list;
};

/** Read-only placeholder: selected labels or empty-state literal. */
export const buildMultiSelectReadOnlyPlaceholder = (selectValue, emptyLabel) => {
    const normalized = normalizeMultiSelectValue(selectValue);
    if (!normalized?.length) {
        return emptyLabel;
    }
    const labels = normalized.map((option) => option.label).filter(Boolean);
    return labels.length ? labels.join(', ') : emptyLabel;
};
